import { useState, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import type { Message, ChatResponse, PersonaConfig, PlanState } from '@/types';
import { PERSONAS } from '@/types';

export function useChat() {
  const [isSending, setIsSending] = useState(false);
  const { currentPersona, activeConversationId, conversations, updateConversation, addConversation, setActiveConversation } =
    useAuthStore();

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isSending) return;

      setIsSending(true);

      const persona = PERSONAS.find((p) => p.id === currentPersona) as PersonaConfig;
      const conversationId = activeConversation?.id;
      const planMode = activeConversation?.planMode;

      // Add user message immediately
      const userMessage: Message = {
        id: `msg_${Date.now()}_user`,
        role: 'user',
        content: content.trim(),
        timestamp: Date.now(),
      };

      if (activeConversation) {
        updateConversation(activeConversation.id, {
          messages: [...activeConversation.messages, userMessage],
        });
      } else {
        const newConv = {
          id: `conv_${Date.now()}`,
          persona: currentPersona,
          title: content.trim().slice(0, 50),
          messages: [userMessage],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        addConversation(newConv);
      }

      try {
        const response: ChatResponse = await api.sendMessage(
          content.trim(),
          conversationId,
          persona.endpoint,
          planMode
        );

        if (response.error) {
          const errorMessage: Message = {
            id: `msg_${Date.now()}_error`,
            role: 'assistant',
            content: `⚠️ ${response.error}`,
            timestamp: Date.now(),
          };
          const convId = activeConversationId || `conv_${Date.now()}`;
          updateConversation(convId, {
            messages: [...(conversations.find((c) => c.id === convId)?.messages || []), errorMessage],
          });
          setIsSending(false);
          return;
        }

        const assistantMessage: Message = {
          id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: response.response,
          sources: response.sources,
          persona: currentPersona,
          timestamp: Date.now(),
        };

        const targetConvId = response.conversationId || activeConversationId || `conv_${Date.now()}`;
        const existingConv = conversations.find((c) => c.id === targetConvId);

        if (existingConv) {
          const updates: Partial<typeof existingConv> = {
            messages: [...existingConv.messages, assistantMessage],
            updatedAt: Date.now(),
          };

          if (currentPersona === 'venture' && response.action) {
            updates.ventureContext = {
              action: response.action,
              sources: response.sources,
              budget: response.budget,
            };
          }

          if (currentPersona === 'plan') {
            if (response.mode) updates.planMode = response.mode;
          }

          updateConversation(targetConvId, updates);
        } else {
          addConversation({
            id: targetConvId,
            persona: currentPersona,
            title: content.trim().slice(0, 50),
            messages: [userMessage, assistantMessage],
            ventureContext:
              currentPersona === 'venture' && response.action
                ? { action: response.action, sources: response.sources, budget: response.budget }
                : undefined,
            planMode: currentPersona === 'plan' ? response.mode : undefined,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          });
        }

        setActiveConversation(targetConvId);
      } catch (err) {
        const errorMessage: Message = {
          id: `msg_${Date.now()}_error`,
          role: 'assistant',
          content: '⚠️ Sorry, something went wrong. Please try again.',
          timestamp: Date.now(),
        };
        const convId = activeConversationId || `conv_${Date.now()}`;
        const conv = conversations.find((c) => c.id === convId);
        if (conv) {
          updateConversation(convId, {
            messages: [...conv.messages, errorMessage],
          });
        }
      } finally {
        setIsSending(false);
      }
    },
    [currentPersona, activeConversation, activeConversationId, conversations, isSending, updateConversation, addConversation, setActiveConversation]
  );

  const planItOut = useCallback(
    async (conversationId: string) => {
      setIsSending(true);
      try {
        const response = await api.planItOut(conversationId);
        if (response.error) {
          const errorMessage: Message = {
            id: `msg_${Date.now()}_error`,
            role: 'assistant',
            content: `⚠️ ${response.error}`,
            timestamp: Date.now(),
          };
          updateConversation(conversationId, {
            messages: [...(conversations.find((c) => c.id === conversationId)?.messages || []), errorMessage],
          });
          return;
        }

        const assistantMessage: Message = {
          id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: response.response,
          timestamp: Date.now(),
        };

        const newConvId = response.newConversationId || conversationId;
        updateConversation(conversationId, {
          messages: [...(conversations.find((c) => c.id === conversationId)?.messages || []), assistantMessage],
        });

        // Switch to plan persona
        useAuthStore.getState().setCurrentPersona('plan');

        // Create or update plan conversation
        const existingPlanConv = conversations.find((c) => c.id === newConvId);
        if (!existingPlanConv) {
          addConversation({
            id: newConvId,
            persona: 'plan',
            title: 'Business Plan',
            messages: [assistantMessage],
            planMode: response.mode || 'ceo',
            createdAt: Date.now(),
            updatedAt: Date.now(),
          });
        }
        setActiveConversation(newConvId);
      } catch (err) {
        console.error('Plan it out failed:', err);
      } finally {
        setIsSending(false);
      }
    },
    [conversations, updateConversation, addConversation, setActiveConversation]
  );

  const advancePlan = useCallback(
    async (conversationId: string) => {
      setIsSending(true);
      try {
        const response = await api.advancePlan(conversationId);
        if (response.error) {
          const errorMessage: Message = {
            id: `msg_${Date.now()}_error`,
            role: 'assistant',
            content: `⚠️ ${response.error}`,
            timestamp: Date.now(),
          };
          updateConversation(conversationId, {
            messages: [...(conversations.find((c) => c.id === conversationId)?.messages || []), errorMessage],
          });
          return;
        }

        const assistantMessage: Message = {
          id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: response.response,
          timestamp: Date.now(),
        };

        updateConversation(conversationId, {
          messages: [...(conversations.find((c) => c.id === conversationId)?.messages || []), assistantMessage],
          planMode: response.mode,
          updatedAt: Date.now(),
        });
      } catch (err) {
        console.error('Advance plan failed:', err);
      } finally {
        setIsSending(false);
      }
    },
    [conversations, updateConversation]
  );

  const getPlanState = useCallback((): PlanState | null => {
    if (currentPersona !== 'plan' || !activeConversation) return null;
    
    // We would typically get this from the last API response, but for now we'll infer
    return {
      mode: activeConversation.planMode || 'ceo',
      modeLabel: activeConversation.planMode?.toUpperCase() || 'CEO',
      nextMode: '',
      navigation: {
        current: 1,
        next: 2,
        previous: 0,
        total: 5,
        step: activeConversation.planMode || 'ceo',
      },
      canAdvance: true,
    };
  }, [currentPersona, activeConversation]);

  return {
    messages: activeConversation?.messages || [],
    isSending,
    sendMessage,
    planItOut,
    advancePlan,
    getPlanState,
    ventureContext: activeConversation?.ventureContext,
    activeConversationId,
  };
}
